<template>
  <div v-if="eventData" class="my-5 m-auto px-0">
    <div class="max-w-screen-xl m-auto px-4">
      <Heading
        :level="1"
        h-class="text-h5 lg:text-h4"
        :title="eventGetters.getTitle(eventData)"
        class="kft-heading--left text-stone-800"
        data-test-id="event-title"
      />
      <div
        class="text-thin text-base my-2 md:line-clamp-2 md:hover:line-clamp-none"
        v-html="eventGetters.getSubtitle(eventData)"
      ></div>
      <div class="flex justify-between items-center mt-4">
        <EventMetaInfo
          :supplier-name="eventGetters.getSupplierName(eventData)"
          :reviews-average="reviewsAverageToDisplay"
          :reviews-type="reviewsTypeToDisplay"
          :reviews-total="reviewsTotalToDisplay"
          :has-review-section="shouldShowReviews"
        />
        <EventShareWishlistButtons
          :event="eventData"
          :is-giftcard="isGiftcard"
        />
      </div>
      <EventGallery
        class="my-5"
        :event="eventData || null"
        :is-giftcard="isGiftcard"
      ></EventGallery>

      <!-- BEGIN::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->
      <div
        :class="{
          'lg:w-2/3': props.isPreview,
        }"
        class="flex flex-col lg:flex-row-reverse gap-4"
      >
        <EventDetailsSidebar v-if="!props.isPreview" />
        <div class="w-auto flex-1 flex-col gap-5 overflow-hidden">
          <div class="w-full">
            <Heading
              :level="2"
              :title="getTitleByCategory('about', eventData)"
              h-class="text-h5"
              class="kft-heading--left mt-8 mb-2"
            ></Heading>
            <div class="md:pr-8">
              <NuxtLazyHydrate when-visible>
                <StrapiContentWrapper
                  :rich-text="eventGetters.getDescription(eventData)"
                />
              </NuxtLazyHydrate>
            </div>
            <EventImportantDetails
              v-if="eventData"
              :is-giftcard="isGiftcard"
              :event-data="eventData as EventDescription"
              :hide-title="false"
            />
            <div v-if="eventGetters.getCourseProgress(eventData)" class="my-8">
              <Heading
                v-if="!isGiftcard"
                :level="2"
                :title="getTitleByCategory('progress', eventData)"
                h-class="text-h5 font-medium"
                class="kft-heading--left mt-8 mb-2 text-stone-800"
              />
              <div class="md:pr-8">
                <NuxtLazyHydrate when-visible>
                  <StrapiContentWrapper
                    :rich-text="eventGetters.getCourseProgress(eventData)"
                  />
                </NuxtLazyHydrate>
              </div>
            </div>
            <div v-if="eventGetters.getCourseTarget(eventData)" class="my-8">
              <Heading
                v-if="!isGiftcard"
                :level="2"
                :title="getTitleByCategory('courseTarget', eventData)"
                h-class="text-h5 font-medium"
                class="kft-heading--left mt-8 mb-2 text-stone-800"
              />
              <div class="md:pr-8">
                <NuxtLazyHydrate when-visible>
                  <StrapiContentWrapper
                    :rich-text="eventGetters.getCourseTarget(eventData)"
                  />
                </NuxtLazyHydrate>
              </div>
            </div>
            <!-- BEGIN::Private Event Info -->
            <NuxtLazyHydrate when-visible>
              <LazyPrivateEventInfo
                v-if="
                  eventGetters.getIsTeamEvent(eventData) &&
                  eventGetters.getPrivateEventDescription(eventData) !== ''
                "
                :description="
                  eventGetters.getPrivateEventDescription(eventData)
                "
              />
            </NuxtLazyHydrate>
            <!-- END::Private Event Info -->

            <!-- BEGIN::Customer Media Gallery -->
            <NuxtLazyHydrate when-visible>
              <EventCustomerMediaGallery
                v-if="eventData"
                :event="eventData"
                @open-review="handleOpenReviewFromGallery"
              />
            </NuxtLazyHydrate>
            <!-- END::Customer Media Gallery -->

          </div>

          <template v-if="addOns && addOns.length > 0">
            <div class="mt-10">
              <EventAddOnsDetails
                :add-ons="addOns"
                :add-directly-to-cart="false"
                @click:addon="handleAddOnClick"
              />
            </div>
          </template>
          <!-- END::Ticket suggestion cards -->
        </div>
      </div>
      <!-- END::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->

      <ErrorBoundary>
        <NuxtLazyHydrate when-visible>
          <ClientOnly>
            <LocationAndMap v-if="!isGiftcard" :kft-event="eventData || null" />
          </ClientOnly>
        </NuxtLazyHydrate>
      </ErrorBoundary>

      <div
        v-if="!isGiftcard"
        id="sectionPartner"
        ref="partnerSection"
        class="mt-10 w-full bg-stone-50 p-4 rounded-lg"
      >
        <NuxtLink
          :to="localePath(`/p/${eventGetters.getSupplierPermalink(eventData)}`)"
        >
          <NuxtLazyHydrate when-visible>
            <UserProfileHeader
              :title-level="2"
              title-h-class="text-h5"
              :subtitle="eventGetters.getSupplierUserName(eventData)"
              :name="eventGetters.getSupplierName(eventData)"
              :image-url="eventGetters.getSupplierLogo(eventData)"
            />
          </NuxtLazyHydrate>
        </NuxtLink>

        <div
          class="text-base text-stone-700 line-clamp-3"
          v-html="eventGetters.getSupplierShortDescription(eventData)"
        ></div>
      </div>
    </div>
    <div
      v-if="shouldShowReviews"
      id="sectionReviews"
      class="product-show-cases !max-w-none py-5 md:py-10 bg-[#F9F7F5] mt-10 scroll-mt-[130px] md:scroll-mt-[160px]"
    >
      <NuxtLazyHydrate when-visible>
        <LazyReviewShowcase
          :average-rating="reviewsAverageToDisplay"
          :review-count="reviewsTotalToDisplay"
          :supplier-name="supplierNameToDisplay"
          :reviews="reviewsToDisplay"
          :review-type="reviewsTypeToDisplay"
          :supplier-created-at="supplierCreatedAtToDisplay"
          :display-item-title="false"
          :event-permalink="eventPermalink"
          :review-breakdown="eventGetters.getReviewBreakdown(eventData)"
          :open-with-review-id="reviewToOpenFromGallery"
          @clear-open-review="reviewToOpenFromGallery = undefined"
        />
      </NuxtLazyHydrate>
    </div>
    <div
      class="max-w-screen-xl px-4 m-auto overflow-hidden md:overflow-inherit"
    >
      <!-- Begin::Supplier events -->
      <div class="product-show-cases [&>div]:my-10">
        <NuxtLazyHydrate when-visible>
          <LazyProductShowcase
            v-if="
              parsedSupplierEvents &&
              parsedSupplierEvents.length > 0 &&
              eventData
            "
            data-test-id="supplier-events"
            :products="parsedSupplierEvents || []"
            :tags="[]"
            :gtm-item-list-id="GtmItemListId.EventPageSameSupplierClasses"
            :title="
              isGiftcard
                ? $t('product.giftcardEventsFromTheSameSupplier')
                : $t('product.eventsFromTheSameSupplier')
            "
            :title-level="2"
            :use-h-tag="true"
            :algolia-insights-client="algoliaInsightsClient"
            algolia-insights-label="Supplier Events - Event Page"
          />
        </NuxtLazyHydrate>
        <!-- End::Supplier events -->

        <RenderOnIntersection
          v-if="
            eventData &&
            eventGetters.getAttendanceMode(eventData) !== 'ONLINE' &&
            !isSubdomain
          "
        >
          <EventSimilarEvents :is-giftcard="isGiftcard" />
        </RenderOnIntersection>

        <RenderOnIntersection
          v-if="
            eventData &&
            eventGetters.getMainCategorySlug(eventData) &&
            !isGiftcard &&
            !isSubdomain
          "
        >
          <EventSimilarOnlineEvents />
        </RenderOnIntersection>
      </div>
      <!-- :gtm-item-list-id="GtmItemListId.EventPageSimilarOnlineClasses" -->
      <!-- End::Similar Online events -->

      <!-- Begin::Similar Box Events -->
      <div v-if="!isSubdomain && !isGiftcard && eventData">
        <NuxtLazyHydrate when-visible>
          <ProductBoxShowcase
            :title="$t('product.boxSimilarLabelOnEventPage')"
            title-class="text-h3"
            class="my-10"
            :category="eventGetters.getMainCategorySlug(eventData)"
            :is-duration-visible="false"
            is-mobile-slider
          />
        </NuxtLazyHydrate>
      </div>
      <!-- End::Similar Box Events -->

      <EventTags v-if="!isSubdomain && eventData" :event="eventData" />

      <NuxtLazyHydrate when-visible>
        <EventBreadcrumbs
          v-if="!isSubdomain && eventData"
          :event-detail="eventData as any"
          :is-giftcard="isGiftcard"
        />
      </NuxtLazyHydrate>
    </div>
  </div>
</template>

<script lang="ts" setup>
import * as qs from 'qs'
import type {
  EventDate,
  EventDescription,
  Supplier,
} from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import useEventSchema from '~/composables/schemaOrg/useEventSchema'
import useSeoHelper from '~/composables/useSeoHelper'
import useProductSchema from '~/composables/schemaOrg/useProductSchema'
import { sendProductDetailView } from '~/composables/useGtmEvents'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import {
  useClearEventDataOnUnmount,
  useEventRedirects,
} from '~/composables/useEvent'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { useTitleByCategory } from '~/composables/product/useGetTitleByCategory'
import type { CartItem } from '~/composables/types/cart'
import { setLastViewedEvent } from '~/utils/lastViewedEvents'
import { mergeKftEventDescriptionSettingsIncludes } from '~/utils/kftEventDescriptionIncludes'
import { supplierGetters } from '~/utils/supplierGetters'

const { getTitleByCategory } = useTitleByCategory()

interface Props {
  eventPermalink?: string
  isGiftcard?: boolean
  isSpecialGiftcard?: boolean
  shouldAddEventSchema?: boolean
  isPreview?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  eventPermalink: undefined,
  isGiftcard: false,
  isSpecialGiftcard: false,
  shouldAddEventSchema: false,
  isPreview: false,
})
const isSubdomain = useIsSubdomain()

const { t: $t } = useI18n()
const $localePath = useLocalePath()
const localePath = $localePath
const { query: queryParams } = useRoute()
const { cart, addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState()
const { client: algoliaInsightsClient } = useAlgoliaSearchInsights()
const buyBoxContainer = ref()
const reviewToOpenFromGallery = ref<string | undefined>(undefined)

const handleOpenReviewFromGallery = (reviewId: string) => {
  reviewToOpenFromGallery.value = reviewId
  nextTick(() => {
    document.getElementById('sectionReviews')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  })
}
const { data: eventDataResponse, error: eventDataLoadError } =
  await useKftApiFetch<KftApiDefaultResponse<EventDescription>>(
    `v1/store/events/${props.eventPermalink}${
      props.isPreview ? `/preview` : ``
    }?${qs.stringify(
      {
        include: mergeKftEventDescriptionSettingsIncludes([
          'address.locality',
          'supplier',
          'supplier.logo',
          'reviews',
          'activePromotion',
          'pricing',
        ]),
      },
      { arrayFormat: 'comma' }
    )}`,
    {
      transform: (res) => {
        // remove phone, email, website, address from supplier data
        if (res?.data?.supplier?.data) {
          const { data } = res.data.supplier
          ;['phone', 'email', 'website', 'address', 'description'].forEach(
            (key) => delete data[key]
          )
        }
        return res
      },
    }
  )

const eventData = useEventData()
eventData.value = eventDataResponse.value?.data
useClearEventDataOnUnmount()

// handle event redirects
await useEventRedirects(
  eventData?.value,
  eventDataLoadError?.value,
  props.isGiftcard,
  props.eventPermalink
)

provide('eventBuyBoxVisibleElements', {
  calendar: true,
  requestButtons: true,
  giftcard: true,
})

// Only provide eventData if it exists to prevent null/undefined injection issues
if (eventData.value) {
  provide('eventData', eventData.value)
}
provide('isSpecialGiftcardEvent', props.isSpecialGiftcard)

provide('openReviewInSidebar', handleOpenReviewFromGallery)

/**  DATA FETCHING **/
const { data: partnerEvents } =
  eventData.value &&
  eventGetters.getSupplierId(eventData.value) &&
  (await useFetchSupplierEvents(
    eventGetters.getSupplierId(eventData.value)!,
    undefined,
    {
      default: () => ref([]),
      params: {
        include: ['supplier'],
      },
    }
  ))

const {
  add_event_to_cart_quantity: quantity,
  add_event_to_cart_date_id: dateId,
  date_type: paramDateType,
} = queryParams

const eventId = eventGetters.getId(eventData?.value)

const { data: addToCardDate, execute: executeFetchAddToCartDate } =
  await useKftApiFetch<KftApiDefaultResponse<EventDate[]>, EventDate>(
    `v1/store/events/${eventId}/dates?search=id:${dateId};${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}&include=children`,
    {
      server: false,
      immediate: false,
      transform: (res) => {
        return res?.data && res.data[0]
      },
    }
  )

const { data: addOnsResponse } = await useKftApiFetch(
  `v1/store/events/${eventId}/add-ons?include=media`
)
const addOns = computed(() => addOnsResponse.value?.data)

// Fetch partner reviews as fallback when event has no reviews
const supplierId = computed(() => eventGetters.getSupplierId(eventData.value))
const hasEventReviews = computed(() => {
  if (!eventData.value) return false
  return (
    eventGetters.getReviewsListRecentFirst(eventData.value).length > 0 &&
    eventGetters.getReviewsTotal(eventData.value) > 0
  )
})

const partnerReviewsFetchResult =
  eventData.value && supplierId.value && !hasEventReviews.value
    ? await useKftApiFetch<KftApiDefaultResponse<Supplier>>(
        `v1/store/supplier/${supplierId.value}?include=reviews`
      )
    : null

const partnerReviewsData = computed(() => {
  if (partnerReviewsFetchResult?.data?.value?.data) {
    return partnerReviewsFetchResult.data.value.data
  }
  return null
})

// Check if partner actually has reviews (not just exists)
const hasPartnerReviews = computed(() => {
  if (!partnerReviewsData.value) return false
  const totalReviews = supplierGetters.getReviewsTotal(partnerReviewsData.value)
  const avgReview = supplierGetters.getReviewsAverage(partnerReviewsData.value)
  return totalReviews > 0 && avgReview > 0
})

const hasThirdPartyReviews = computed(() => {
  const partner =
    eventGetters.getSupplier(eventData.value) || partnerReviewsData.value
  return (
    partner?.google_avg_review != null &&
    partner?.google_avg_review > 0 &&
    partner?.google_total_reviews != null &&
    partner?.google_total_reviews > 0
  )
})

/** COMPUTED **/
const parsedSupplierEvents = computed(() => {
  if (!partnerEvents.value || !(partnerEvents.value as any)?.data?.length)
    return []
  return transformToAlgoliaEvent(partnerEvents.value as any)
    .filter((e) => e.hashed_id !== eventData.value?.id)
    .slice(0, 10)
})

// Computed properties for reviews with fallback to partner reviews
const reviewsToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsListRecentFirst(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getReviewsList(partnerReviewsData.value)
  }
  return []
})

const reviewsTypeToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return 'event'
  }
  if (hasPartnerReviews.value) {
    return 'partner'
  }
  if (hasThirdPartyReviews.value) {
    return 'third-party'
  }
  return 'event'
})

const reviewsAverageToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsAverage(eventData.value) || 0
  }
  if (hasPartnerReviews.value) {
    return String(supplierGetters.getReviewsAverage(partnerReviewsData.value))
  }
  // Fallback to Google reviews from supplier data
  const partner =
    eventGetters.getSupplier(eventData.value) || partnerReviewsData.value
  if (partner?.google_avg_review) {
    return String(partner.google_avg_review)
  }
  return 0
})

const reviewsTotalToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsTotal(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getReviewsTotal(partnerReviewsData.value)
  }
  // Fallback to Google reviews from supplier data
  const supplier =
    eventGetters.getSupplier(eventData.value) || partnerReviewsData.value
  if (supplier?.google_total_reviews) {
    return supplier.google_total_reviews
  }
  return 0
})

const supplierNameToDisplay = computed(() => {
  return eventGetters.getSupplierName(eventData.value) || ''
})

const supplierCreatedAtToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getSupplierCreatedAt(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getCreatedAt(partnerReviewsData.value)
  }
  return ''
})

const shouldShowReviews = computed(() => {
  // Only show review section if event has reviews, not partner reviews
  return hasEventReviews.value
})

const shouldTryToFetchAddToCartLinks = computed(
  () =>
    quantity &&
    dateId &&
    eventId &&
    !cart.value.items.find(
      (i) => i.id === dateId && i.quantity === Number(quantity)
    )
)

const handleAddToCartLinks = async () => {
  const qtyTickets = Math.min(
    Number(quantity),
    addToCardDate.value?.availableTicketsQuantity || 1
  )

  if (addToCardDate.value && addToCardDate.value?.dateType && eventData.value) {
    const { dateType } = addToCardDate.value
    const dateIsPrivate = dateType === 'PRIVATE' || paramDateType === 'PRIVATE'

    const pricingsResponse = await useFetchPricings(ref(eventData.value.id), {
      dateId: ref(addToCardDate.value.id),
      quantity: ref(qtyTickets),
      isPrivate: ref(dateIsPrivate),
    })

    if (!pricingsResponse) {
      return
    }

    await book(
      parseEventDateToCartItem(
        eventData.value,
        qtyTickets,
        pricingsResponse,
        addToCardDate as Ref<EventDate>,
        ref([]),
        dateIsPrivate
      )
    )
  }
}

const book = async (params: CartItem) => {
  await addItem(params)
  toggleCartSidebar()
}

const { script: eventSchemas } = useEventSchema(
  eventData.value ? [eventData.value] : []
)
const { script: productSchemas } = useProductSchema(
  eventData.value ? [eventData.value] : []
)

const handleAddOnClick = () => {
  if (!buyBoxContainer?.value) return

  const offsetHeight = buyBoxContainer.value.offsetTop ?? 0

  window.scrollTo({
    // subract 130 to account for navbar
    top: offsetHeight - 130,
    behavior: 'smooth',
  })
}

onMounted(async () => {
  try {
    // Add small delay to stabilize DOM
    await nextTick()

    if (shouldTryToFetchAddToCartLinks.value) {
      await executeFetchAddToCartDate()
      handleAddToCartLinks()
    }

    if (eventData.value) {
      sendProductDetailView(eventData.value)
    }

    try {
      if (eventData.value?.id) {
        setLastViewedEvent(eventData.value.id)
      }
    } catch (error) {
      console.warn('Failed to set last viewed event:', error)
    }
  } catch (e) {
    console.error('Error in EventDetails onMounted:', e)
  }
})

const seoOptions = computed(() => ({
  head: {
    script: [
      props.shouldAddEventSchema ? eventSchemas : null,
      props.isGiftcard ? productSchemas : null,
    ].filter(Boolean),
    title: eventData.value ? eventGetters.getTitle(eventData.value) : '',
  },
  shouldBeIndexed: !props.isPreview,

  meta: {
    title:
      (eventData.value ? eventGetters.getTitle(eventData.value) : '') +
      ' | ' +
      $t('general.titles.base'),
    'og:title':
      (eventData.value ? eventGetters.getTitle(eventData.value) : '') +
      ' | ' +
      $t('general.titles.base'),
    description: eventData.value
      ? eventGetters.getMetaDescription(eventData.value)
      : '',
    'og:description': eventData.value
      ? eventGetters.getMetaDescription(eventData.value)
      : '',
    ...(eventData.value &&
      eventGetters.getOpenGraphImage(eventData.value) && {
        'og:image': eventGetters.getOpenGraphImage(eventData.value),
      }),
    'og:type': 'product',
    ...(eventData.value &&
      eventGetters.getMinimumPriceAmount(eventData.value) && {
        'product:price:amount': String(
          Number(eventGetters.getMinimumPriceAmount(eventData.value)) / 100
        ),
      }),
    'product:price:currency': eventData.value
      ? eventGetters.getMinimumPriceCurrency(eventData.value) || ''
      : '',
    'og:availability': 'instock',
  },
}))
useSeoHelper(seoOptions)
</script>

<style lang="postcss" scoped>
#sectionPartner {
  @apply max-w-[812px];
}
</style>
